import { Avatar, AvatarGroup } from '@c4ads/c4blocks';
import { Tooltip } from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { ReactNode } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { tableHelpers } from '../../../../helpers/tableHelpers';

const renderConcluded = (isConcluded: boolean | undefined | null): ReactNode => {
    const icon = isConcluded ? <CheckCircleOutlineIcon color="primary" /> : <HighlightOffIcon color="disabled" />;
    const title = isConcluded ? 'Concluded' : 'Ongoing';

    return (
        <Tooltip title={title} enterDelay={400}>
            {icon}
        </Tooltip>
    );
};

const CommodityCell = ({ params }: { params: GridRenderCellParams }) => (
    <AvatarGroup max={4}>
        {params.row.commodities.map(
            ({ id, species, unit }: { id: string; species: { commonName: string }; unit: string }) => (
                <Tooltip key={id} title={species.commonName + ' ' + unit}>
                    <Avatar>{species.commonName.trim().slice(0, 1)}</Avatar>
                </Tooltip>
            )
        )}
    </AvatarGroup>
);

const columns: GridColDef[] = [
    {
        field: 'docket',
        headerName: 'Case Reference Number',
        headerAlign: 'center',
        align: 'center',
        width: 180,
        groupable: false,
    },
    {
        field: 'administrativeLevel1',
        headerName: 'District',
        headerAlign: 'center',
        width: 180,
        groupable: false,
        valueGetter: ({ row }) => row.administrativeLevel1?.name,
    },
    {
        field: 'court',
        headerName: 'Court',
        headerAlign: 'center',
        width: 180,
        groupable: false,
        valueGetter: ({ row }) => row.court?.name,
    },
    {
        field: 'name',
        headerName: 'Name',
        headerAlign: 'center',
        width: 180,
        groupable: false,
    },
    {
        field: 'firstEvent',
        type: 'date',
        headerName: 'First Event',
        headerAlign: 'center',
        width: 200,
        groupable: false,
        valueGetter: ({ row }) => (row.firstEvent ? `${row.firstEvent.date} (${row.firstEvent.stage})` : '--'),
    },
    {
        field: 'latestEvent',
        type: 'date',
        headerName: 'Latest Event',
        headerAlign: 'center',
        width: 200,
        groupable: false,
        valueGetter: ({ row }) => (row.latestEvent ? `${row.latestEvent.date} (${row.latestEvent.stage})` : '--'),
    },
    {
        field: 'isConcluded',
        type: 'boolean',
        headerName: 'Concluded',
        headerAlign: 'center',
        align: 'center',
        width: 180,
        groupable: false,
        renderCell: ({ value }) => renderConcluded(value),
    },
    {
        field: 'caseDurationDays',
        type: 'number',
        headerName: 'Duration (days)',
        headerAlign: 'center',
        width: 160,
        groupable: false,
        renderCell: ({ value }) => tableHelpers.renderNumber(value),
    },
    {
        field: 'commodities',
        headerName: 'Commodities',
        headerAlign: 'center',
        width: 200,
        groupable: false,
        align: 'center',
        valueGetter: ({ row }) =>
            row.commodities
                .map(({ species, unit }) => ((species?.commonName || '') + ' ' + (unit || '')).trim())
                .join(' / '),
        renderCell: (params: GridRenderCellParams) => <CommodityCell params={params} />,
    },
    {
        field: 'defenseAttorneyCount',
        type: 'number',
        headerName: 'Defense Attorney Count',
        headerAlign: 'center',
        align: 'center',
        width: 180,
        groupable: false,
        renderCell: ({ value }) => tableHelpers.renderNumber(value),
    },
];

export default columns;
